import React, { useContext, useEffect, useRef } from 'react';
import { RoomEvent, Track } from 'livekit-client';
import { FetchContext } from '../context/FetchContext';
import { LocalVideoRecording } from './LocalVideoRecording';
import { getUploadProgress } from './LocalRecordingStorage';

type Props = {
  room: any;
  livekitToken: string;
  localRecordingEnabled: boolean;
  recordingInProgress: boolean;
  assetIdRef: { current: string | null };
  assetId?: string | null;
  screenRecordingLocalTrackId?: string | null;
  getRecordingElapsedMs?: () => number;
  isHDQuality?: boolean;
  setScreenShareUploadProgress?: (progress: number | null) => void;
};

type ActiveSession = {
  recorder: LocalVideoRecording;
  serverTrackId: string;
  livekitTrackSid: string | null;
};

type PendingStart = {
  livekitTrackSid: string | null;
  startOffsetMs: number;
};

const LocalScreenShareRecordingCompat = (props: Props) => {
  const {
    room,
    livekitToken,
    localRecordingEnabled,
    recordingInProgress,
    assetIdRef,
    assetId,
    screenRecordingLocalTrackId,
    getRecordingElapsedMs,
    isHDQuality,
    setScreenShareUploadProgress,
  } = props;

  const fetchContext = useContext(FetchContext);

  const recordingInProgressRef = useRef(recordingInProgress);
  const localRecordingEnabledRef = useRef(localRecordingEnabled);
  const screenRecordingLocalTrackIdRef = useRef<string | null>(screenRecordingLocalTrackId ?? null);
  const getRecordingElapsedMsRef = useRef<(() => number) | undefined>(getRecordingElapsedMs);
  const isHDQualityRef = useRef<boolean | undefined>(isHDQuality);
  const setScreenShareUploadProgressRef = useRef<Props['setScreenShareUploadProgress']>(setScreenShareUploadProgress);
  const activeSessionRef = useRef<ActiveSession | null>(null);
  const startingRef = useRef(false);
  const stopRequestedRef = useRef(false);
  const pendingStartRef = useRef<PendingStart | null>(null);
  const trackedTrackIdsRef = useRef<Set<string>>(new Set());
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastAssetIdSeenRef = useRef<string | null>(null);

  useEffect(() => {
    recordingInProgressRef.current = recordingInProgress;
  }, [recordingInProgress]);

  useEffect(() => {
    localRecordingEnabledRef.current = localRecordingEnabled;
  }, [localRecordingEnabled]);

  useEffect(() => {
    screenRecordingLocalTrackIdRef.current = screenRecordingLocalTrackId ?? null;
  }, [screenRecordingLocalTrackId]);

  useEffect(() => {
    getRecordingElapsedMsRef.current = getRecordingElapsedMs;
  }, [getRecordingElapsedMs]);

  useEffect(() => {
    isHDQualityRef.current = isHDQuality;
  }, [isHDQuality]);

  useEffect(() => {
    setScreenShareUploadProgressRef.current = setScreenShareUploadProgress;
  }, [setScreenShareUploadProgress]);

  const emitUploadProgress = (progress: number | null) => {
    try {
      setScreenShareUploadProgressRef.current?.(progress);
    } catch {}
  };

  const stopProgressPolling = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const pollProgress = async () => {
    if (!setScreenShareUploadProgressRef.current) return;

    const trackIds = Array.from(trackedTrackIdsRef.current);
    if (trackIds.length === 0) {
      emitUploadProgress(null);
      stopProgressPolling();
      return;
    }

    let sum = 0;
    let count = 0;
    let allComplete = true;

    for (const trackId of trackIds) {
      try {
        const { progress, isComplete } = await getUploadProgress(trackId);
        sum += progress;
        count++;
        if (!isComplete) allComplete = false;
      } catch {
        allComplete = false;
      }
    }

    emitUploadProgress(count > 0 ? sum / count : null);

    if (allComplete && !recordingInProgressRef.current && !startingRef.current) {
      stopProgressPolling();
    }
  };

  const startProgressPolling = () => {
    if (!setScreenShareUploadProgressRef.current) return;
    if (progressIntervalRef.current) return;
    progressIntervalRef.current = setInterval(() => {
      void pollProgress();
    }, 1000);
    void pollProgress();
  };

  const resolveAssetId = () => {
    const fromProp = typeof assetId === 'string' ? assetId.trim() : '';
    if (fromProp) return fromProp;
    const fromRef = assetIdRef?.current;
    if (typeof fromRef === 'string' && fromRef.trim() !== '') return fromRef.trim();
    return null;
  };

  // Reset screen-share progress when the recording asset changes.
  // NOTE: `assetIdRef` is a ref and may change without triggering a render, so we also
  // run this check on `recordingInProgress` transitions.
  useEffect(() => {
    if (!setScreenShareUploadProgressRef.current) return;
    const currentAssetId = resolveAssetId();
    if (!currentAssetId) return;
    if (lastAssetIdSeenRef.current === currentAssetId) return;

    lastAssetIdSeenRef.current = currentAssetId;
    trackedTrackIdsRef.current.clear();
    stopProgressPolling();
    emitUploadProgress(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, recordingInProgress]);

  const getOffsetMs = () => {
    try {
      return Math.max(0, Math.round(getRecordingElapsedMsRef.current?.() ?? 0));
    } catch {
      return 0;
    }
  };

  const getRoomName = () => {
    try {
      return (room as any)?.name || (room as any)?.roomName || '';
    } catch {
      return '';
    }
  };

  const getParticipantIdentifier = () => {
    try {
      return (room as any)?.localParticipant?.identity || '';
    } catch {
      return '';
    }
  };

  const getCurrentScreenSharePublication = () => {
    try {
      const localParticipant = (room as any)?.localParticipant;
      const bySource = localParticipant?.getTrackPublication?.(Track.Source.ScreenShare);
      if (bySource) return bySource;

      const pubs: any = localParticipant?.videoTrackPublications;
      if (pubs && typeof pubs.forEach === 'function') {
        let found: any = null;
        pubs.forEach((pub: any) => {
          if (found) return;
          if (pub?.source === Track.Source.ScreenShare) found = pub;
        });
        return found;
      }
    } catch {}
    return null;
  };

  const stopActiveSession = async (reason: 'unpublished' | 'recording_stopped' | 'unmount') => {
    pendingStartRef.current = null;
    const session = activeSessionRef.current;
    if (!session) {
      if (startingRef.current) stopRequestedRef.current = true;
      return;
    }

    activeSessionRef.current = null;
    stopRequestedRef.current = false;

    const roomName = getRoomName();
    const participantIdentifier = getParticipantIdentifier();
    const endOffsetMs = getOffsetMs();

    try {
      await session.recorder.stop();
    } catch (e) {
      console.warn('[LocalScreenShareRecordingCompat] Failed stopping local screen-share recorder', e);
    }

    try {
      if (!roomName || !participantIdentifier || !livekitToken) return;
      await fetchContext.authAxios.post(
        '/recording/screen/session/end',
        {
          roomName,
          participantIdentifier,
          trackId: session.serverTrackId,
          livekitScreenTrackSid: session.livekitTrackSid,
          endOffsetMs,
          reason,
        },
        { headers: { Authorization: `Bearer ${livekitToken}` } }
      );
    } catch (e) {
      // Best-effort: never break recording UX if this fails.
      console.warn('[LocalScreenShareRecordingCompat] /recording/screen/session/end failed', e);
    }
  };

	  const startSessionIfPossible = async (
	    pub: any,
	    opts?: { startOffsetMs?: number; livekitTrackSid?: string | null }
	  ) => {
	    if (!room || !livekitToken) return;
	    if (!recordingInProgressRef.current) return;
	    if (!localRecordingEnabledRef.current) return;
	    if (activeSessionRef.current || startingRef.current) return;

    const roomName = getRoomName();
    const participantIdentifier = getParticipantIdentifier();
    if (!roomName || !participantIdentifier) return;

    const livekitTrackSid: string | null = (opts?.livekitTrackSid ??
      ((pub?.trackSid ?? pub?.sid ?? null) as any)) as any;
	    const startOffsetMs = Number.isFinite(opts?.startOffsetMs as any)
	      ? Math.max(0, Math.round(opts?.startOffsetMs as any))
	      : getOffsetMs();
	    const trackIdFromFirestore = screenRecordingLocalTrackIdRef.current;
	    const preparedTrackId =
	      typeof trackIdFromFirestore === 'string' && trackIdFromFirestore.trim() !== ''
	        ? trackIdFromFirestore.trim()
	        : null;

	    // Server contract expects a prepared trackId for this session.
	    // If it's not available yet, don't start recording a session we can't upload.
	    if (!preparedTrackId) {
	      pendingStartRef.current = { livekitTrackSid, startOffsetMs };
	      return;
	    }

	    startingRef.current = true;
	    stopRequestedRef.current = false;
	    pendingStartRef.current = null;

	    let nextTrackId: string | null = null;
	    try {
	      try {
	        console.log('[LocalScreenShareRecordingCompat] /recording/screen/session/start', {
	          roomName,
	          participantIdentifier,
	          trackId: preparedTrackId,
	          livekitScreenTrackSid: livekitTrackSid || '',
	          startOffsetMs,
	        });
	      } catch {}

	      const { data } = await fetchContext.authAxios.post(
	        '/recording/screen/session/start',
	        {
	          roomName,
	          participantIdentifier,
	          trackId: preparedTrackId,
	          livekitScreenTrackSid: livekitTrackSid || '',
	          startOffsetMs,
	        },
	        { headers: { Authorization: `Bearer ${livekitToken}` } }
	      );

	      nextTrackId =
	        (typeof data?.nextScreenRecordingLocalTrackId === 'string' && data.nextScreenRecordingLocalTrackId) ||
	        (typeof data?.nextTrackId === 'string' && data.nextTrackId) ||
	        null;
	    } catch (e) {
	      console.warn('[LocalScreenShareRecordingCompat] /recording/screen/session/start failed', e);
	    }

	    if (nextTrackId && typeof nextTrackId === 'string' && nextTrackId.trim() !== '') {
	      // Optimistically update the local ref for fast stop/start cycles; Firestore will also update.
	      screenRecordingLocalTrackIdRef.current = nextTrackId.trim();
	    }

	    const cleanServerTrackId = preparedTrackId;

	    const recorder = new LocalVideoRecording({
	      room,
	      fetchContext,
	      uploadToken: livekitToken,
	      serverTrackId: cleanServerTrackId,
	      videoSources: [Track.Source.ScreenShare],
	      audioSources: [Track.Source.ScreenShareAudio],
	      isHDQuality: isHDQualityRef.current,
	      fps: 30,
	      chunkDurationSec: 10,
      batchSize: 50,
      onFatalError: (err) => {
        console.warn('[LocalScreenShareRecordingCompat] Fatal error in screen-share local recorder', err);
        void stopActiveSession('recording_stopped');
      },
    });

    try {
      await recorder.start();
    } catch (e) {
      console.warn('[LocalScreenShareRecordingCompat] Failed to start local screen-share recorder', e);
      startingRef.current = false;
      return;
	    }

	    activeSessionRef.current = { recorder, serverTrackId: cleanServerTrackId, livekitTrackSid };
	    startingRef.current = false;

	    if (setScreenShareUploadProgressRef.current) {
	      trackedTrackIdsRef.current.add(cleanServerTrackId);
	      startProgressPolling();
	    }

    if (stopRequestedRef.current) {
      void stopActiveSession('unpublished');
      return;
    }

    const stillPublished = (() => {
      try {
        const currentPub = getCurrentScreenSharePublication();
        if (!currentPub) return false;
        if (!livekitTrackSid) return true;
        return currentPub?.trackSid === livekitTrackSid;
      } catch {
        return true;
      }
    })();

    if (!stillPublished) void stopActiveSession('unpublished');
  };

  // Listen to local ScreenShare publish/unpublish to start/stop per-session recording.
  useEffect(() => {
    if (!room) return;

    const onPublished = (publication: any) => {
      try {
        if (publication?.source !== Track.Source.ScreenShare) return;
        void startSessionIfPossible(publication);
      } catch {}
    };

    const onUnpublished = (publication: any) => {
      try {
        if (publication?.source !== Track.Source.ScreenShare) return;
        void stopActiveSession('unpublished');
      } catch {}
    };

    try {
      room.on(RoomEvent.LocalTrackPublished, onPublished);
      room.on(RoomEvent.LocalTrackUnpublished, onUnpublished);
    } catch {
      try {
        room.on('localTrackPublished', onPublished);
        room.on('localTrackUnpublished', onUnpublished);
      } catch {}
    }

    return () => {
      try {
        room.off(RoomEvent.LocalTrackPublished, onPublished);
        room.off(RoomEvent.LocalTrackUnpublished, onUnpublished);
      } catch {
        try {
          room.off('localTrackPublished', onPublished);
          room.off('localTrackUnpublished', onUnpublished);
        } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, livekitToken, fetchContext]);

  // If recording starts while already sharing, begin a session at that moment (offset ~0).
  useEffect(() => {
    if (!room) return;
    if (!recordingInProgress) return;
    if (!localRecordingEnabled) return;
    const pub = getCurrentScreenSharePublication();
    if (pub) void startSessionIfPossible(pub);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, recordingInProgress, localRecordingEnabled]);

  // If screen share started but the server-provided trackId wasn't ready yet, retry once it appears.
  useEffect(() => {
    if (!room) return;
    if (!recordingInProgressRef.current) return;
    if (!localRecordingEnabledRef.current) return;
    if (activeSessionRef.current || startingRef.current) return;
    const pending = pendingStartRef.current;
    if (!pending) return;
    const pub = getCurrentScreenSharePublication();
    if (!pub) return;
    const currentSid: string | null = (pub?.trackSid ?? pub?.sid ?? null) as any;
    if (pending.livekitTrackSid && currentSid && pending.livekitTrackSid !== currentSid) {
      pendingStartRef.current = null;
      return;
    }
    void startSessionIfPossible(pub, { startOffsetMs: pending.startOffsetMs, livekitTrackSid: currentSid });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenRecordingLocalTrackId, room]);

  // If recording stops while sharing, stop the active session and record an end offset.
  useEffect(() => {
    if (!room) return;
    if (recordingInProgress) return;
    void stopActiveSession('recording_stopped');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, recordingInProgress]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      stopProgressPolling();
      emitUploadProgress(null);
      void stopActiveSession('unmount');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default LocalScreenShareRecordingCompat;
